import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector, useDispatch } from 'react-redux';
import { findPanelWiringLines, findBellTrafoForBranch, updateBellTrafoPosition } from '../../../modules/wiring';
import { StateSnapshotCommand } from '../../undoRedo';

// Global flag for M key state (panel move mode)
export const panelMoveModeRef = { current: false };

export function usePanelMove(scene, camera, domElement) {
  const selectedObjectIds = useSelector((state) => state.selection.selectedObjectIds);
  const gridMode = useSelector((state) => state.mods.gridMode);
  const gridStep = useSelector((state) => state.mods.gridStep);
  // Refs so the mousemove closure always sees the latest values without
  // re-subscribing its listener.
  const gridModeRef = useRef(gridMode);
  const gridStepRef = useRef(gridStep);
  useEffect(() => { gridModeRef.current = gridMode; }, [gridMode]);
  useEffect(() => { gridStepRef.current = gridStep; }, [gridStep]);
  const movingPanelRef = useRef(null);
  const moveBeforeRef = useRef(null);   // undo/redo: sürükleme öncesi durum (entries)
  const moveTargetsRef = useRef(null);  // etkilenen nesneler (panel + wire'lar + trafo/title)
  const raycaster = new THREE.Raycaster();
  const plane = new THREE.Plane();
  const offset = new THREE.Vector3();
  const planeIntersect = new THREE.Vector3();
  const originalPosition = useRef(new THREE.Vector3());
  const panelDir = useRef(1); // Panel'in yönü (1 veya -1)

  useEffect(() => {
    if (!scene || !camera || !domElement) {
      
      return;
    }

    

    const getMouse = (event) => {
      const rect = domElement.getBoundingClientRect();
      return {
        x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
        y: -((event.clientY - rect.top) / rect.height) * 2 + 1,
      };
    };

    // ✅ Undo/redo: panel + bağlı wire/trafo/title nesnelerini topla (geniş aday seti;
    // hareket etmeyenlerin before=after olur, no-op).
    const collectTargets = (panel) => {
      const targets = [panel];
      const panelId = panel.userData?.id;
      const seen = new Set();
      const addWire = (w) => { if (w && !seen.has(w.uuid)) { seen.add(w.uuid); targets.push(w); } };
      (panel.userData?.connectedWires || []).forEach(wireId => {
        scene.traverse(o => { if (o.type === 'Line' && o.userData?.id === wireId) addWire(o); });
      });
      findPanelWiringLines(scene, panelId).forEach(addWire);
      (panel.userData?.outs || []).forEach(out => {
        if (out?.branchType === 'bell' && out.branchID) {
          const t = findBellTrafoForBranch(scene, out.branchID);
          if (t) targets.push(t);
        }
      });
      scene.traverse(o => {
        if (o.userData?.type === 'wireTitle' &&
            (o.userData?.panelId === panelId || o.userData?.panelID === panelId)) targets.push(o);
      });
      return targets;
    };

    const startCapture = (panel) => {
      moveTargetsRef.current = collectTargets(panel);
      moveBeforeRef.current = StateSnapshotCommand.capture(moveTargetsRef.current);
    };

    // Sürükleme bittiğinde after'ı al + değiştiyse undo/redo komutu ekle.
    const finishMove = () => {
      if (!moveBeforeRef.current || !moveTargetsRef.current) return;
      const after = StateSnapshotCommand.capture(moveTargetsRef.current);
      if (StateSnapshotCommand.changed(moveBeforeRef.current, after) && window.undoRedoManager) {
        const mgr = window.undoRedoManager;
        const tx = mgr.beginTransaction('Panel Taşı');
        mgr.addCommand(new StateSnapshotCommand(moveBeforeRef.current, after, 'PANEL_MOVE'));
        mgr.commitTransaction(tx);
      }
      moveBeforeRef.current = null;
      moveTargetsRef.current = null;
    };

    const onKeyDown = (e) => {
      if (e.key === 'm' || e.key === 'M') {
        panelMoveModeRef.current = true;

      }
    };

    const onKeyUp = (e) => {
      if (e.key === 'm' || e.key === 'M') {
        panelMoveModeRef.current = false;
        
        // M tuşu bırakıldığında hareketi sonlandır
        if (movingPanelRef.current) {
          finishMove(); // ✅ undo/redo komutu
          movingPanelRef.current = null;
        }
      }
    };

    const onMouseDown = (event) => {
      
      
      
      // M tuşu basılı değilse çık
      if (!panelMoveModeRef.current) return;

      // Event'i durdur ve öncelik al
      event.stopPropagation();
      event.preventDefault();

      const mouse = getMouse(event);
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      

      if (movingPanelRef.current) {
        // Zaten taşıma aktif - tıklamayı sonlandır
        finishMove(); // ✅ undo/redo komutu
        movingPanelRef.current = null;
        return;
      }

      // Panel click'i bul (type: 'panel' / 'mainPanel' / 'electricalPanel')
      const panelHit = intersects.find(i => {
        
        
        
        
        // Hatch direkt offsetGroup'un çocuğu olabilir
        const directParent = i.object.parent; // offsetGroup
        const grandParent = i.object.parent?.parent; // group
        
        // electricalPanel tipini kontrol et (seçim kontrolü yok artık)
        const isPanel = (directParent && 
               (directParent.userData?.type === 'panel' || 
                directParent.userData?.type === 'mainPanel' ||
                directParent.userData?.type === 'electricalPanel')) ||
              (grandParent && 
               (grandParent.userData?.type === 'panel' || 
                grandParent.userData?.type === 'mainPanel' ||
                grandParent.userData?.type === 'electricalPanel'));
        
        if (isPanel) {
          
        }
        return isPanel;
      });

      if (panelHit) {
        const panelGroup = (panelHit.object.parent?.userData?.type === 'panel' || 
                           panelHit.object.parent?.userData?.type === 'mainPanel' ||
                           panelHit.object.parent?.userData?.type === 'electricalPanel')
          ? panelHit.object.parent
          : panelHit.object.parent?.parent; // group
        
        
        
        
        movingPanelRef.current = panelGroup;
        startCapture(panelGroup); // ✅ undo/redo: sürükleme öncesi durumu yakala
        originalPosition.current.copy(panelGroup.position);
        
        // Panel'in dir değerini al
        panelDir.current = panelGroup.userData?.dir || 1;
        
        // Plane'i oluştur
        plane.setFromNormalAndCoplanarPoint(
          camera.getWorldDirection(new THREE.Vector3()),
          panelGroup.position
        );
        
        raycaster.ray.intersectPlane(plane, planeIntersect);
        offset.copy(planeIntersect).sub(panelGroup.position);
      }
    };

    const onMouseMove = (event) => {
      if (!movingPanelRef.current || !panelMoveModeRef.current) return;

      const mouse = getMouse(event);
      raycaster.setFromCamera(mouse, camera);
      raycaster.ray.intersectPlane(plane, planeIntersect);

      const targetPosition = planeIntersect.clone().sub(offset);
      const panelId = movingPanelRef.current.userData.id;

      // Eski pozisyonu kaydet (wire offset hesabı için)
      const oldPosition = movingPanelRef.current.position.clone();

      // Panel açısına göre hareket yönünü hesapla
      // Panel kendi ekseni boyunca (duvara paralel) hareket etmeli
      const angle = movingPanelRef.current.rotation.z;

      // Hareket yönü vektörü - panel ekseni boyunca (duvara paralel)
      const moveDirX = -Math.sin(angle);
      const moveDirY = Math.cos(angle);

      // Mouse hareketini hesapla
      const movementX = targetPosition.x - originalPosition.current.x;
      const movementY = targetPosition.y - originalPosition.current.y;

      // Hareketi panel ekseni yönüne project et (dot product)
      let projectedDistance = movementX * moveDirX + movementY * moveDirY;

      // ─── Grid snap (F7/F9 ile toggle): projected distance'ı step'in katına oturt.
      //   Panel sadece kendi ekseninde hareket ettiği için eksen boyunca 5 birim
      //   kademeli ilerleme gerekiyor — wire çizimindeki ile aynı mantık.
      if (gridModeRef.current) {
        const step = gridStepRef.current || 5;
        projectedDistance = Math.round(projectedDistance / step) * step;
      }

      // Yeni pozisyonu hesapla - sadece panel ekseni boyunca
      movingPanelRef.current.position.x = originalPosition.current.x + moveDirX * projectedDistance;
      movingPanelRef.current.position.y = originalPosition.current.y + moveDirY * projectedDistance;
      movingPanelRef.current.position.z = originalPosition.current.z;
      
      // Panel hareket miktarını hesapla
      const deltaX = movingPanelRef.current.position.x - oldPosition.x;
      const deltaY = movingPanelRef.current.position.y - oldPosition.y;
      
      // Panele bağlı wire'ları bul - hem connectedWires'tan hem de findPanelWiringLines'tan
      const connectedWireIds = movingPanelRef.current.userData.connectedWires || [];
      let wiringLines = [];
      const addedWireIds = new Set();

      // 1. connectedWires'tan wire'ları al
      if (connectedWireIds.length > 0) {
        connectedWireIds.forEach(wireId => {
          scene.traverse(obj => {
            if (obj.type === 'Line' && obj.userData?.id === wireId) {
              wiringLines.push(obj);
              addedWireIds.add(wireId);
            }
          });
        });
      }

      // 2. findPanelWiringLines'tan da wire'ları al (çakışmaları önle)
      const panelWiringLines = findPanelWiringLines(scene, panelId);
      panelWiringLines.forEach(wireLine => {
        const wireId = wireLine.userData?.id;
        if (wireId && !addedWireIds.has(wireId)) {
          wiringLines.push(wireLine);
          addedWireIds.add(wireId);
        }
      });
      
      // Her wire'ın panele bağlı kenarını taşı - kenar boyu ve açısı korunur
      wiringLines.forEach(wireLine => {
        if (wireLine.geometry && wireLine.geometry.attributes.position) {
          const positions = wireLine.geometry.attributes.position.array;
          const vertexCount = positions.length / 3;

          if (vertexCount >= 2) {
            // Wire'ın hangi ucunun panele bağlı olduğunu belirle
            // İlk vertex panele yakınsa → çıkış hattı (ilk iki vertex taşınır)
            // Son vertex panele yakınsa → giriş hattı (son iki vertex taşınır)
            // ✅ Mesh-FP FIX: panel (fpMesh-local) ile wire (attach edilmiş → WORLD geometry)
            // farklı frame'de olabilir. Mesafe karşılaştırmasını WORLD frame'de yap; yoksa
            // mesafeler fpMesh offset'i kadar şişip eşiği aşıyor ve wire taşınmıyordu.
            const panelPos = new THREE.Vector3();
            movingPanelRef.current.getWorldPosition(panelPos);
            wireLine.updateWorldMatrix(true, false);

            // İlk ve son vertex'in WORLD pozisyonları
            const firstWorld = new THREE.Vector3(
              positions[0], positions[1], positions[2] || 0
            ).applyMatrix4(wireLine.matrixWorld);
            const lastWorld = new THREE.Vector3(
              positions[positions.length - 3],
              positions[positions.length - 2],
              positions[positions.length - 1] || 0
            ).applyMatrix4(wireLine.matrixWorld);

            // Panele olan mesafeleri hesapla (world)
            const distToFirst = firstWorld.distanceTo(panelPos);
            const distToLast = lastWorld.distanceTo(panelPos);

            // ✅ FIX: Branch hatlarını (buat bağlantıları) filtrele
            const sourceType = wireLine.userData?.sourceType;
            const isBranchLine = sourceType === 'socketBuat' || sourceType === 'lightingBuat' || 
                               sourceType === 'socketVertex' || sourceType === 'lightingVertex';

            if (isBranchLine) {
              return;
            }

            // ✅ FIX: Mesafe kontrolü (Eğer her iki uç da panele uzaksa taşıma)
            // Panel boyutu yaklaşık 50-100 birim, güvenli bir eşik değeri (örn. 100) kullanalım
            const CONNECTION_THRESHOLD = 100;
            if (distToFirst > CONNECTION_THRESHOLD && distToLast > CONNECTION_THRESHOLD) {
              return;
            }

            // İlk vertex panele daha yakınsa çıkış hattı, son vertex yakınsa giriş hattı
            const isOutputWire = distToFirst < distToLast;

            if (isOutputWire) {
              // Çıkış hattı - İLK iki vertex taşınmalı (panelden çıkan kenar)
              // İlk vertex
              positions[0] += deltaX;
              positions[1] += deltaY;

              // İkinci vertex
              if (vertexCount >= 2) {
                positions[3] += deltaX;
                positions[4] += deltaY;
              }
            } else {
              // Besleme hattı - SON iki vertex taşınmalı (panele giren kenar)
              const lastIndex = positions.length - 3;
              const secondLastIndex = positions.length - 6;

              // Son vertex
              positions[lastIndex] += deltaX;
              positions[lastIndex + 1] += deltaY;

              // Sondan bir önceki vertex
              if (secondLastIndex >= 0) {
                positions[secondLastIndex] += deltaX;
                positions[secondLastIndex + 1] += deltaY;
              }
            }

            wireLine.geometry.attributes.position.needsUpdate = true;
            wireLine.geometry.computeBoundingSphere();
          }
        }
      });
      
      // ✅ BellTrafo'ları da taşı (panele bağlı zil hatlarında)
      const outs = movingPanelRef.current.userData.outs || [];
      outs.forEach(out => {
        if (out && out.branchType === 'bell' && out.branchID) {
          const bellTrafo = findBellTrafoForBranch(scene, out.branchID);
          if (bellTrafo) {
            // BellTrafo'yu aynı delta kadar hareket ettir
            bellTrafo.position.x += deltaX;
            bellTrafo.position.y += deltaY;
            bellTrafo.updateMatrixWorld(true);
          }
        }
      });

      // ✅ WireTitle'ları kontrol et - sadece ilk kenardakiler taşınsın
      scene.traverse(obj => {
        if (obj.userData?.type === 'wireTitle' &&
            (obj.userData?.panelId === panelId || obj.userData?.panelID === panelId)) {

          // Bu title'ın bağlı olduğu wire'ı bul
          const titleWireId = obj.userData?.wireId || obj.userData?.lineId;
          let matchingWire = null;

          if (titleWireId) {
            matchingWire = wiringLines.find(w =>
              w.userData?.id === titleWireId ||
              w.userData?.wireID === titleWireId
            );
          }

          // Wire bulunduysa
          if (matchingWire && matchingWire.geometry?.attributes?.position) {
            const positions = matchingWire.geometry.attributes.position;

            // Wire'ın ilk iki noktasını al (ilk kenar - panel ile sürüklenen kenar)
            const firstPoint = new THREE.Vector3(
              positions.getX(0),
              positions.getY(0),
              positions.getZ(0)
            );
            const secondPoint = new THREE.Vector3(
              positions.getX(1),
              positions.getY(1),
              positions.getZ(1)
            );

            // Title'ın world pozisyonunu al
            const titleWorldPos = new THREE.Vector3();
            obj.getWorldPosition(titleWorldPos);

            // Title'ın ilk kenara olan mesafesini hesapla
            // İlk kenar üzerindeki en yakın noktayı bul
            const edgeDir = new THREE.Vector3().subVectors(secondPoint, firstPoint);
            const edgeLength = edgeDir.length();
            edgeDir.normalize();

            const toTitle = new THREE.Vector3().subVectors(titleWorldPos, firstPoint);
            const projection = toTitle.dot(edgeDir);

            // Title ilk kenar üzerinde mi kontrol et (0 ile edgeLength arasında ve kenara yakın)
            const isOnFirstEdge = projection >= -5 && projection <= edgeLength + 5;

            // Kenara olan dik mesafe
            const closestPointOnEdge = firstPoint.clone().add(edgeDir.clone().multiplyScalar(
              Math.max(0, Math.min(projection, edgeLength))
            ));
            const distanceToEdge = titleWorldPos.distanceTo(closestPointOnEdge);

            // Sadece ilk kenardaki title'ları taşı (mesafe eşiği: 50 birim)
            if (isOnFirstEdge && distanceToEdge < 50) {
              obj.position.x += deltaX;
              obj.position.y += deltaY;
            }

            // wireVertices'i her durumda güncelle (tüm title'lar için)
            const newVertices = [];
            for (let i = 0; i < positions.count; i++) {
              newVertices.push(new THREE.Vector3(
                positions.getX(i),
                positions.getY(i),
                positions.getZ(i)
              ));
            }
            obj.userData.wireVertices = newVertices;
          } else {
            // Wire bulunamadıysa ve title ilk kenardaysa wireVertices'i delta kadar ötele
            if (obj.userData.wireVertices && obj.userData.wireVertices.length >= 2) {
              // İlk kenarı kontrol et
              const firstPoint = obj.userData.wireVertices[0];
              const secondPoint = obj.userData.wireVertices[1];

              const fp = firstPoint instanceof THREE.Vector3 ? firstPoint : new THREE.Vector3(firstPoint.x, firstPoint.y, firstPoint.z || 0);
              const sp = secondPoint instanceof THREE.Vector3 ? secondPoint : new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z || 0);

              const titleWorldPos = new THREE.Vector3();
              obj.getWorldPosition(titleWorldPos);

              const edgeDir = new THREE.Vector3().subVectors(sp, fp);
              const edgeLength = edgeDir.length();
              edgeDir.normalize();

              const toTitle = new THREE.Vector3().subVectors(titleWorldPos, fp);
              const projection = toTitle.dot(edgeDir);
              const isOnFirstEdge = projection >= -5 && projection <= edgeLength + 5;

              const closestPointOnEdge = fp.clone().add(edgeDir.clone().multiplyScalar(
                Math.max(0, Math.min(projection, edgeLength))
              ));
              const distanceToEdge = titleWorldPos.distanceTo(closestPointOnEdge);

              if (isOnFirstEdge && distanceToEdge < 50) {
                obj.position.x += deltaX;
                obj.position.y += deltaY;

                // İlk iki vertex'i de güncelle
                obj.userData.wireVertices = obj.userData.wireVertices.map((v, idx) => {
                  if (idx < 2) {
                    if (v instanceof THREE.Vector3) {
                      return new THREE.Vector3(v.x + deltaX, v.y + deltaY, v.z);
                    } else {
                      return new THREE.Vector3(
                        (v.x || 0) + deltaX,
                        (v.y || 0) + deltaY,
                        v.z || 0
                      );
                    }
                  }
                  return v;
                });
              }
            }
          }
        }
      });
      
      // userData'daki basePoint'i güncelle (yeni obje oluştur - frozen obje sorunu için)
      if (movingPanelRef.current.userData.basePoint) {
        movingPanelRef.current.userData.basePoint = {
          ...movingPanelRef.current.userData.basePoint,
          x: movingPanelRef.current.position.x,
          y: movingPanelRef.current.position.y
        };
      }
    };

    const onEscape = (e) => {
      if (e.key === 'Escape' && movingPanelRef.current) {
        // Escape ile iptal - yakalanan before durumundan TAM geri al (panel + wire'lar +
        // trafo/title). Komut EKLENMEZ (iptal). before yoksa eski panel-only revert.
        if (moveBeforeRef.current) {
          new StateSnapshotCommand(moveBeforeRef.current, moveBeforeRef.current).undo(scene);
        } else {
          movingPanelRef.current.position.copy(originalPosition.current);
          if (movingPanelRef.current.userData.basePoint) {
            movingPanelRef.current.userData.basePoint.x = originalPosition.current.x;
            movingPanelRef.current.userData.basePoint.y = originalPosition.current.y;
          }
        }
        moveBeforeRef.current = null;
        moveTargetsRef.current = null;
        movingPanelRef.current = null;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('keydown', onEscape);
    
    // Test: Her mousedown'u logla
    const testMouseDown = (e) => {
      
      if (panelMoveModeRef.current) {
        
      }
    };
    
    domElement.addEventListener('mousedown', testMouseDown, true);
    domElement.addEventListener('mousedown', onMouseDown, true); // Capture phase kullan
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('keydown', onEscape);
      domElement.removeEventListener('mousedown', testMouseDown, true);
      domElement.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [scene, camera, domElement, selectedObjectIds]);
}
